import React, { useState } from 'react';
import VentasPanel from './context/VentasPanel';
import AtencionPanel from './context/AtencionPanel';
import SoportePanel from './context/SoportePanel';
import AuditorPanel from './context/AuditorPanel';

function normalizarArea(...valores) {
  const texto = valores
    .filter(Boolean)
    .map((v) => String(v).toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, ''))
    .join(' ');

  if (texto.includes('ventas') || texto.includes('venta') || texto.includes('comercial')) return 'ventas';
  if (texto.includes('soporte') || texto.includes('tecnico') || texto.includes('tecnica')) return 'soporte';
  if (texto.includes('atencion') || texto.includes('administrativo') || texto.includes('admin')) return 'atencion';
  return 'atencion';
}

// ─── Motivos de cierre para el modal Finalizar ───
const MOTIVOS_CIERRE = [
  'Problema resuelto',
  'Cliente desiste',
  'Derivado a otra área',
  'No responde',
  'Otro',
];

function FinalizarCard({
  currentPermissions,
  toggleIA,
  cambiandoIA,
  isIAActive,
  transferirConversacion,
  finalizarConversacion,
  cerrarConversacion,
}) {
  const [showModal, setShowModal] = useState(false);
  const [motivoSeleccionado, setMotivoSeleccionado] = useState('');
  const [finalizando, setFinalizando] = useState(false);

  const handleFinalizar = async () => {
    if (!motivoSeleccionado) return;
    setFinalizando(true);
    try {
      // Si existe la función unificada, usarla. Si no, fallback al cerrar clásico.
      if (finalizarConversacion) {
        await finalizarConversacion(motivoSeleccionado);
      } else if (cerrarConversacion) {
        await cerrarConversacion();
      }
      setShowModal(false);
      setMotivoSeleccionado('');
    } catch (e) {
      console.error('Error finalizando:', e);
    } finally {
      setFinalizando(false);
    }
  };

  return (
    <div className="ctx-card">
      <div className="ctx-card-header">
        <span className="ctx-card-title">Gestión de conversación</span>
      </div>

      {currentPermissions.toggleIA && (
        <button className="service-action" onClick={toggleIA} disabled={cambiandoIA}>
          {cambiandoIA ? '⏳ Procesando...' : isIAActive ? '⛔ Anular IA' : '🤖 Reactivar IA'}
        </button>
      )}

      {currentPermissions.transferConversation && (
        <button className="service-action" onClick={transferirConversacion}>
          🔄 Transferir a otra área
        </button>
      )}

      {currentPermissions.closeConversation && (
        <button
          className="service-action"
          onClick={() => setShowModal(true)}
          style={{ color: '#f59e0b', fontWeight: 600 }}
        >
          🏁 Finalizar conversación
        </button>
      )}

      {/* ─── Mini-modal motivo de cierre ─── */}
      {showModal && (
        <div
          style={{
            marginTop: 10,
            padding: 14,
            background: 'var(--bg)',
            border: '1px solid var(--border-light)',
            borderRadius: 12,
          }}
        >
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 10 }}>
            ¿Motivo de cierre?
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {MOTIVOS_CIERRE.map((motivo) => (
              <label
                key={motivo}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '6px 8px',
                  borderRadius: 8,
                  cursor: 'pointer',
                  background: motivoSeleccionado === motivo ? '#3b82f622' : 'transparent',
                  border: motivoSeleccionado === motivo ? '1px solid #3b82f644' : '1px solid transparent',
                  transition: 'all 0.15s',
                }}
              >
                <input
                  type="radio"
                  name="motivo-cierre"
                  value={motivo}
                  checked={motivoSeleccionado === motivo}
                  onChange={() => setMotivoSeleccionado(motivo)}
                  style={{ accentColor: '#3b82f6' }}
                />
                <span style={{ fontSize: 12, color: 'var(--text)' }}>{motivo}</span>
              </label>
            ))}
          </div>

          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button
              onClick={() => {
                setShowModal(false);
                setMotivoSeleccionado('');
              }}
              style={{
                flex: 1,
                padding: '8px 0',
                background: 'transparent',
                border: '1px solid var(--border-light)',
                borderRadius: 8,
                color: 'var(--muted)',
                fontSize: 12,
                cursor: 'pointer',
              }}
            >
              Cancelar
            </button>
            <button
              onClick={handleFinalizar}
              disabled={!motivoSeleccionado || finalizando}
              style={{
                flex: 1,
                padding: '8px 0',
                background: motivoSeleccionado ? '#f59e0b' : '#f59e0b44',
                border: 'none',
                borderRadius: 8,
                color: motivoSeleccionado ? '#000' : '#f59e0b88',
                fontSize: 12,
                fontWeight: 600,
                cursor: motivoSeleccionado ? 'pointer' : 'not-allowed',
                opacity: finalizando ? 0.6 : 1,
              }}
            >
              {finalizando ? '⏳ Finalizando...' : '🏁 Finalizar'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function ContextPanel({
  conversacionActiva,
  categoriaActiva,
  areasHabilitadas = [],
  currentPermissions = {},
  datosCliente,
  ventaActual,
  accionRapidaVentas,
  abrirModalVentas,
  confirmarVenta,
  actualizarVentaLocal,
  guardarEstadoOperativo,
  guardandoEstado,
  atencionActual,
  accionRapidaAtencion,
  actualizarAtencionLocal,
  soporteActual,
  accionRapidaSoporte,
  actualizarSoporteLocal,
  diagnosticoBCM,
  ejecutandoAccionBCM,
  mensajeBCM,
  reinicioConfirmado,
  ejecutarDiagnostico,
  reiniciarONU,
  verificarReinicioManual,
  desbloquearBCM,
  confirmarSoporte,
  onVerFactura,
  onGenerarLinkMP,
  tomarConversacion,
  transferirConversacion,
  cerrarConversacion,
  finalizarConversacion,
  toggleIA,
  cambiandoIA,
}) {
  if (!conversacionActiva) return <aside className="context" />;

  const areaActiva = normalizarArea(categoriaActiva, conversacionActiva.area, conversacionActiva.categoria);
  const esVentas = areaActiva === 'ventas';
  const esAtencion = areaActiva === 'atencion';
  const esSoporte = areaActiva === 'soporte';

  const areaHabilitada = areasHabilitadas.includes(areaActiva) || areasHabilitadas.length === 0;

  const nombre = conversacionActiva.nombre_cliente || datosCliente?.nombre || 'Sin nombre';
  const inicial = nombre?.[0] || 'C';
  const telefono = conversacionActiva.telefono || '-';
  const isClosed = conversacionActiva.estado === 'cerrada';
  const isIAActive = conversacionActiva.ia_activa !== false;

  const estado = datosCliente?.estado || 'Sin datos';
  const isActivo = estado === 'Activo';
  const isSuspendido = estado === 'Suspendido' || String(estado || '').toLowerCase().includes('suspend');
  const isLead = !datosCliente || datosCliente.plan === 'Sin datos';
  const svcColor = isActivo ? '#4ade80' : isSuspendido ? '#ef4444' : '#60a5fa';
  const svcBg = isActivo ? '#22c55e22' : isSuspendido ? '#ef444422' : '#3b82f622';
  const svcLabel = isLead ? 'Lead' : isActivo ? 'Cliente activo' : isSuspendido ? 'Suspendido' : estado;

  const fmt = (value, fallback = '-') => {
    if (value === null || value === undefined) return fallback;
    const text = String(value).trim();
    return text || fallback;
  };

  return (
    <aside className="context">
      <div className="ctx-scroll">
        <div className="ctx-card">
          <div className="ctx-card-header"><span className="ctx-card-title">Información del cliente</span></div>
          <div className="client-info">
            <div className="client-avatar" style={{ background: `linear-gradient(135deg, ${svcColor}, var(--accent))` }}>{inicial}</div>
            <div>
              <div className="client-name">{nombre}</div>
              <span className="status-badge" style={{ background: svcBg, color: svcColor }}>{svcLabel}</span>
            </div>
          </div>
          <div className="ctx-details">
            <div className="ctx-row"><span className="ctx-label">Área</span><span className="ctx-value">{areaActiva}</span></div>
            <div className="ctx-row"><span className="ctx-label">Teléfono</span><span className="ctx-value">{telefono}</span></div>
            {datosCliente ? (
              <>
                <div className="ctx-row"><span className="ctx-label">Plan</span><span className="ctx-value">{fmt(datosCliente.plan)}</span></div>
                <div className="ctx-row"><span className="ctx-label">Estado</span><span className="ctx-value" style={{ color: svcColor }}>{estado}</span></div>
                <div className="ctx-row"><span className="ctx-label">Deuda</span><span className="ctx-value" style={{ color: (datosCliente.deuda || 0) > 0 ? '#ef4444' : '#4ade80' }}>${datosCliente.deuda ?? 0}</span></div>
                <div className="ctx-row"><span className="ctx-label">Saldo a favor</span><span className="ctx-value">${datosCliente.saldo_a_favor ?? 0}</span></div>
              </>
            ) : (
              <div style={{ fontSize: 11, color: 'var(--muted)', padding: '4px 0' }}>Cargando datos...</div>
            )}
          </div>
        </div>

        {!isClosed && (
          <div className="ctx-card">
            <div className="ctx-card-header"><span className="ctx-card-title">Acciones rápidas</span></div>
            {currentPermissions.takeConversation && <button className="action-btn primary" onClick={tomarConversacion}>✋ Tomar conversación</button>}
            {esVentas && areaHabilitada && <button className="action-btn success" onClick={() => abrirModalVentas('confirmar')}>✅ Revisar venta detectada por IA</button>}
            {currentPermissions.transferConversation && <button className="action-btn secondary" onClick={transferirConversacion}>🔄 Transferir</button>}
          </div>
        )}

        {!areaHabilitada && (
          <div className="ctx-card">
            <div className="ctx-card-header"><span className="ctx-card-title">Área no habilitada</span></div>
            <div className="empty-note">Tu usuario no tiene habilitada el área {areaActiva}.</div>
          </div>
        )}

        {areaHabilitada && esVentas && (
          <VentasPanel
            conversacionActiva={conversacionActiva}
            nombre={nombre}
            ventaActual={ventaActual}
            accionRapidaVentas={accionRapidaVentas}
            abrirModalVentas={abrirModalVentas}
            confirmarVenta={confirmarVenta}
            actualizarVentaLocal={actualizarVentaLocal}
            isClosed={isClosed}
          />
        )}

        {areaHabilitada && esAtencion && (
          <AtencionPanel
            datosCliente={datosCliente}
            atencionActual={atencionActual}
            accionRapidaAtencion={accionRapidaAtencion}
            actualizarAtencionLocal={actualizarAtencionLocal}
            guardarEstadoOperativo={guardarEstadoOperativo}
            guardandoEstado={guardandoEstado}
            conversacionActiva={conversacionActiva}
            onVerFactura={onVerFactura}
            onGenerarLinkMP={onGenerarLinkMP}
            diagnosticoBCM={diagnosticoBCM}
            estado={estado}
            svcColor={svcColor}
            svcBg={svcBg}
          />
        )}

        {areaHabilitada && esSoporte && (
          <SoportePanel
            datosCliente={datosCliente}
            soporteActual={soporteActual}
            accionRapidaSoporte={accionRapidaSoporte}
            actualizarSoporteLocal={actualizarSoporteLocal}
            guardarEstadoOperativo={guardarEstadoOperativo}
            guardandoEstado={guardandoEstado}
            conversacionActiva={conversacionActiva}
            currentPermissions={currentPermissions}
            diagnosticoBCM={diagnosticoBCM}
            ejecutandoAccionBCM={ejecutandoAccionBCM}
            mensajeBCM={mensajeBCM}
            reinicioConfirmado={reinicioConfirmado}
            ejecutarDiagnostico={ejecutarDiagnostico}
            reiniciarONU={reiniciarONU}
            verificarReinicioManual={verificarReinicioManual}
            desbloquearBCM={desbloquearBCM}
            confirmarSoporte={confirmarSoporte}
            estado={estado}
            svcColor={svcColor}
            svcBg={svcBg}
          />
        )}

        <AuditorPanel areaActiva={areaActiva} />

        <div className="ctx-card">
          <div className="ctx-card-header"><span className="ctx-card-title">Notas del cliente</span><button className="icon-btn-sm">+</button></div>
          <div className="empty-note">Sin notas registradas</div>
        </div>

        {!isClosed && (
          <FinalizarCard
            currentPermissions={currentPermissions}
            toggleIA={toggleIA}
            cambiandoIA={cambiandoIA}
            isIAActive={isIAActive}
            transferirConversacion={transferirConversacion}
            finalizarConversacion={finalizarConversacion}
            cerrarConversacion={cerrarConversacion}
          />
        )}
      </div>
    </aside>
  );
}

export default React.memo(ContextPanel);

